/**
 * The globals helper reads global variables, that were loaded before the app start:
 * main.ts injects the globals and session info loaded from the backend,
 * which then are globally available through the helper functions.
 *
 * This file is part of Eplan
 * (c) Kaden und Partner AG
 */

import { User } from '@/models/User';
import { reactive, type Ref } from 'vue';

export interface AppGlobals {
    allowed_file_types: any;
    environment: string;
    bg_enabled: boolean;
    decision_ruling_status_id: number;
    frontend_base: string;
    login_method: string;
    mapurl: string;
    oereblexurl: string;
    oereburl: string;
    show_under_development_info: boolean;
    statement_status_id: number;
    app_version: string;
    eservices_url: string;
    iamregisterurl: string;
    session_lifetime?: number;
}

export interface AppSession {
    user: User | null;
    csrfToken: string;
}
/** reactive singleton object with global variables, injected on app startup */
const globals = reactive<AppGlobals>({} as AppGlobals);
/** reactive singleton object with session variables, injected on app startup */
const session = reactive<AppSession>({} as AppSession);

/**
 * Takes a raw object with app globals, and sets them in the globals
 * reactive object. Should be set by the app init process, when
 * the globals are loaded from the backend.
 *
 * @param newGlobals
 */
export function setGlobals(newGlobals: AppGlobals) {
    Object.assign(globals, newGlobals);
}

/**
 * Returns a reactive object with the global variables
 */
export function useGlobals(): AppGlobals {
    return globals;
}

/**
 * Takes a raw object with app session data, and sets them in the session
 * reactive object. Should be set by the app init process, when
 * the session data are loaded from the backend.
 *
 * @param newSession
 */
export function setSession(newSession: AppSession) {
    session.user = newSession.user ? new User().set(newSession.user) : null;
    session?.user?.commit();
    session.csrfToken = newSession.csrfToken ?? null;
}

/**
 * Returns a reactive object with the session variables
 */
export function useSession(): AppSession {
    return session as AppSession;
}

/**
 * Returns a global variable value from the globals object delivered by auth/init
 * @param {string} key The global var name
 * @returns the value or null
 */
export function globalVar(key: keyof AppGlobals) {
    return useGlobals()[key];
}

/**
 * Returns the authenticated user, or null
 */
export function useAuthUser(): User | null {
    return session.user as User | null;
}

export function isAuthenticated(): boolean {
    return useAuthUser() !== null;
}
